import { Component, computed, inject, input, output, signal, ViewChild } from '@angular/core';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { AddressModel, CreateAddressRequest } from '../../../../core/models/data/address.model';
import { AddressTypes } from '../../../../core/enums/data.enums';
import { AddressPipeSecondaryAddressFormat, ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { OrganizationService } from '../../../../core/services/organization.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { OrganizationsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { AddressPipe } from '../../../../core/pipes/address/address.pipe';
import { OrganizationAddressFormModalComponent } from '../organization-address-form-modal/organization-address-form-modal.component';

@Component({
  selector: 'ms-organization-addresses',
  standalone: true,
  imports: [
    FaDuotoneIconComponent,
    ConfirmationModalComponent,
    InfoModalComponent,
    OrganizationAddressFormModalComponent,
    AddressPipe
  ],
  templateUrl: './organization-addresses.component.html',
  styleUrl: './organization-addresses.component.scss'
})
export class OrganizationAddressesComponent {

  private organizationService = inject(OrganizationService);
  private bootstrapService = inject(BootstrapService);

  public organization = input<OrganizationModel | undefined>(undefined);
  public organizationUpdated = output<OrganizationModel>();

  @ViewChild('removeAddressModal') removeAddressModalRef!: ConfirmationModalComponent;
  @ViewChild('addressInfoModal') addressInfoModalRef!: InfoModalComponent;
  @ViewChild('addressFormModal') addressFormModalRef!: OrganizationAddressFormModalComponent;

  public OrganizationsMessagingConstants = OrganizationsMessagingConstants;
  public AddressPipeSecondaryAddressFormat = AddressPipeSecondaryAddressFormat;

  public addresses = computed<AddressModel[]>(() => this.organization()?.addresses ?? []);
  public hasAddresses = computed<boolean>(() => this.addresses().length > 0);
  public isSubmitting = signal<boolean>(false);

  private addressPendingRemoval = signal<AddressModel | undefined>(undefined);

  public openAddressForm(): void {
    this.addressFormModalRef.openModal();
  }

  public openRemoveConfirmation(address: AddressModel): void {
    this.addressPendingRemoval.set(address);
    const message = OrganizationsMessagingConstants.ORGANIZATIONS_REMOVE_ADDRESS_CONFIRMATION()
      .replace('{addressType}', address.addressType);
    this.removeAddressModalRef.modalType.set(ModalTypes.Question);
    this.removeAddressModalRef.message.set(message);
    this.bootstrapService.manageModal('#removeAddressModal', ModalActions.Show);
  }

  public async confirmRemoveAddress(): Promise<void> {
    const target = this.addressPendingRemoval();
    const organizationId = this.organization()?.organizationId;
    if (!target || !organizationId) {
      this.bootstrapService.manageModal('#removeAddressModal', ModalActions.Hide);
      return;
    }

    this.isSubmitting.set(true);
    try {
      const remainingAddresses: CreateAddressRequest[] = this.addresses()
        .filter(a => a.addressId !== target.addressId)
        .map(a => this.toRequest(a));

      const result = await this.organizationService.updateOrganizationAddresses(organizationId, { addresses: remainingAddresses });

      this.bootstrapService.manageModal('#removeAddressModal', ModalActions.Hide);

      if (result.success && result.data) {
        this.organizationUpdated.emit(result.data);
        this.addressInfoModalRef.modalType.set(ModalTypes.Success);
        this.addressInfoModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_ADDRESS_REMOVE_SUCCESS());
      } else {
        this.addressInfoModalRef.modalType.set(ModalTypes.Failure);
        this.addressInfoModalRef.message.set(result.message ?? OrganizationsMessagingConstants.ORGANIZATIONS_ADDRESS_REMOVE_FAILURE());
      }
      this.bootstrapService.manageModal('#addressInfoModal', ModalActions.Show);
    } finally {
      this.isSubmitting.set(false);
      this.addressPendingRemoval.set(undefined);
    }
  }

  public cancelRemoveAddress(): void {
    this.bootstrapService.manageModal('#removeAddressModal', ModalActions.Hide);
    this.addressPendingRemoval.set(undefined);
  }

  public closeInfoModal(): void {
    this.bootstrapService.manageModal('#addressInfoModal', ModalActions.Hide);
  }

  public onAddressSaved(updatedOrganization: OrganizationModel): void {
    this.organizationUpdated.emit(updatedOrganization);
    this.addressInfoModalRef.modalType.set(ModalTypes.Success);
    this.addressInfoModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_ADDRESS_SAVE_SUCCESS());
    this.bootstrapService.manageModal('#addressInfoModal', ModalActions.Show);
  }

  public onAddressSaveFailed(message: string | undefined): void {
    this.addressInfoModalRef.modalType.set(ModalTypes.Failure);
    this.addressInfoModalRef.message.set(message ?? OrganizationsMessagingConstants.ORGANIZATIONS_ADDRESS_SAVE_FAILURE());
    this.bootstrapService.manageModal('#addressInfoModal', ModalActions.Show);
  }

  public addressTypeIcon(type: AddressTypes): string {
    switch (type) {
      case AddressTypes.Billing: return 'file-invoice-dollar';
      case AddressTypes.Mailing: return 'envelope';
      case AddressTypes.Physical: return 'location-dot';
    }
  }

  private toRequest(address: AddressModel): CreateAddressRequest {
    return {
      type: address.addressType,
      primaryAddress: address.primaryAddress,
      secondaryAddress: address.secondaryAddress,
      city: address.city,
      stateProvinceId: address.stateProvince.stateProvinceId,
      primaryZip: address.primaryZip,
      secondaryZip: address.secondaryZip
    };
  }
}
